"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { apiPost } from "@/utils/apiClient";

type ProsConsResponse = {
  status: number;
  message: string;
  pros: string[];
  cons: string[];
};

type Props = {
  schemeCode: string | null;
  schemeCatId: string | null;
  kra: string | null;
};

const ProsConsCard = ({ schemeCode, schemeCatId, kra }: Props) => {
  const [pros, setPros] = useState<string[]>([]);
  const [cons, setCons] = useState<string[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const triggerRef = useRef<HTMLDivElement | null>(null);

  const fetchProsCons = useCallback(async () => {
    try {
      const payload = {
        scheme_code: schemeCode || "",
        scheme_category_id: schemeCatId || "",
        kra: kra || "",
      };

      const data = await apiPost<ProsConsResponse>("schemeProsCons", payload);

      if (data.status === 1) {
        setPros(data.pros || []);
        setCons(data.cons || []);
      } else {
        console.error("No data found.");
      }
    } catch (err) {
      console.error("Failed to fetch data.", err);
    }
  }, [schemeCode, schemeCatId, kra]);

  useEffect(() => {
    const currentRef = triggerRef.current;

    if (!currentRef || hasFetched) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasFetched(true);
          setShouldRender(true);
          fetchProsCons();
        }
      },
      {
        root: null,
        rootMargin: "400px",
        threshold: 0.1,
      }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [hasFetched, fetchProsCons]);

  if (!shouldRender || pros.length === 0 || cons.length === 0) {
    return (
      <>
        <div ref={triggerRef} className="h-1 w-full" />
      </>
    );
  }

  return (
    <>
      <div ref={triggerRef} className="h-1 w-full" /> {/* Scroll trigger */}
      <section className="flex flex-col md:flex-row gap-5 sm:gap-6 mb-12">
        {/* Pros */}
        <div className="flex-1 rounded-4xl bg-[#F0FFFC] p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <Image src="/next_assets/images/scheme-details/pros-img.svg" alt="Pros of this fund" width={40} height={40} className="w-6 h-6 sm:w-10 sm:h-10" />
            <p className="text-[#46CC6B] font-bold text-base sm:text-lg lg:text-2xl">Pro’s</p>
          </div>
          <ul className="text-2xs sm:text-base font-medium text-primaryBlack space-y-3">
            {pros.map((item, index) => (
              <li key={index} className="flex items-start gap-3 before:relative before:shrink-0 before:top-2 before:w-2 before:h-2 before:rounded-full before:bg-[#46CC6B]">
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Cons */}
        <div className="flex-1 rounded-4xl bg-[#FFF0F0] p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <Image src="/next_assets/images/scheme-details/cons-img.svg" alt="Cons of this fund" width={40} height={40} className="w-6 h-6 sm:w-10 sm:h-10" />
            <p className="text-[#FF5A54] font-bold text-base sm:text-lg lg:text-2xl">Con’s</p>
          </div>
          <ul className="text-2xs sm:text-base font-medium text-primaryBlack space-y-3">
            {cons.map((item, index) => (
              <li key={index} className="flex items-start gap-3 before:relative before:shrink-0 before:top-2 before:w-2 before:h-2 before:rounded-full before:bg-[#FF5A54]">
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
};

export default ProsConsCard;
